import { FormattedMessage } from 'react-intl'
import { shallowEqual, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'

import AnalyticsDetailCard from '@components/Analytics/DetailCard/DetailCard'
import { Hr } from '@components/UI/Hr/Hr.styled'
import { AnalyticsCustomPrice } from '@components/UI/Price/Price'
import { CPM_EXPLAINED, TRIBE_PRICING_PAGE_PRE_PURCHASE_METRICS_UTM } from '@constants'
import { IApplicationState } from '@store'
import { selectPrePurchaseAnalyticsTikTok } from '@store/prePurchaseAnalytics'
import { selectSubmissionId } from '@store/router'
import { selectSubmission, selectSubTotalCurrency } from '@store/submissions'
import Theme from '@theme'
import { Container, Link } from '@tribegroup'
import { abbreviateNumberOrDash } from '@utils'
import { isNumber } from '../PrePurchase'
import { AnalyticsPrePurchaseGrid } from '../PrePurchase.styled'

const externalLink = TRIBE_PRICING_PAGE_PRE_PURCHASE_METRICS_UTM

const AnalyticsPrePurchaseTikTok = () => {
  const match = useRouteMatch()

  const { followers, prePurchase, currency } = useSelector((state: IApplicationState) => {
    const submissionId = selectSubmissionId(match)!
    const { identity_followers_count, identity_id } = selectSubmission(state, submissionId)

    return {
      followers: identity_followers_count,
      currency: selectSubTotalCurrency(state, submissionId),
      prePurchase: selectPrePurchaseAnalyticsTikTok(state, identity_id),
    }
  }, shallowEqual)

  const estimatedCpe = prePurchase?.est_cpe
  const estimatedCpv = prePurchase?.est_cpv
  const estimatedCpm = prePurchase?.est_cpm

  return (
    <>
      <AnalyticsPrePurchaseGrid>
        <AnalyticsDetailCard
          title="submission.prePurchaseAnalytics.tiktok.avgEngagement"
          content={abbreviateNumberOrDash(prePurchase?.avg_engagement)}
          elementName="pre-purchase-avg-engagement"
          productMetricGroup="pre_purchase"
          productMetricValue="avg_engagement"
          featured
        />
        {estimatedCpe && (
          <AnalyticsDetailCard
            title="submission.prePurchaseAnalytics.tiktok.estCPE"
            content={<AnalyticsCustomPrice currency={currency} quantity={estimatedCpe} />}
            elementName="pre-purchase-est-cpe"
            productMetricGroup="pre_purchase"
            productMetricValue="est_cpe"
            featured
          />
        )}

        <AnalyticsDetailCard
          title="submission.prePurchaseAnalytics.tiktok.avgViews"
          content={abbreviateNumberOrDash(prePurchase?.avg_video_views)}
          elementName="pre-purchase-avg-views"
          productMetricGroup="pre_purchase"
          productMetricValue="avg_video_views"
          externalLink={externalLink}
          featured
        />
      </AnalyticsPrePurchaseGrid>
      <Hr backgroundColor={Theme.grey400Color} />
      <Container topOuterSpacing={1.75}>
        <AnalyticsPrePurchaseGrid>
          <AnalyticsDetailCard
            title="submission.prePurchaseAnalytics.tiktok.followers"
            content={abbreviateNumberOrDash(followers)}
            elementName="pre-purchase-followers"
            productMetricGroup="pre_purchase"
            productMetricValue="followers"
            externalLink={externalLink}
          />
          <AnalyticsDetailCard
            title="submission.prePurchaseAnalytics.tiktok.avgLikes"
            content={abbreviateNumberOrDash(prePurchase?.avg_likes)}
            elementName="pre-purchase-avg-likes"
            productMetricGroup="pre_purchase"
            productMetricValue="avg_likes"
          />
          <AnalyticsDetailCard
            title="submission.prePurchaseAnalytics.tiktok.avgComments"
            content={abbreviateNumberOrDash(prePurchase?.avg_comments)}
            elementName="pre-purchase-avg-comments"
            productMetricGroup="pre_purchase"
            productMetricValue="avg_comments"
          />
          <AnalyticsDetailCard
            title="submission.prePurchaseAnalytics.tiktok.avgShares"
            content={abbreviateNumberOrDash(prePurchase?.avg_shares)}
            elementName="pre-purchase-avg-shares"
            productMetricGroup="pre_purchase"
            productMetricValue="avg_shares"
          />

          <AnalyticsDetailCard
            title="submission.prePurchaseAnalytics.tiktok.avgEngagementPct"
            content={
              isNumber(prePurchase?.avg_engagement_pct) && (
                <FormattedMessage
                  id="core.text.percent"
                  values={{ value: prePurchase?.avg_engagement_pct }}
                />
              )
            }
            elementName="pre-purchase-avg-engagement-pct"
            productMetricGroup="pre_purchase"
            productMetricValue="avg_engagement_pct"
          />

          <AnalyticsDetailCard
            title="submission.prePurchaseAnalytics.tiktok.avgViewPct"
            content={
              isNumber(prePurchase?.avg_video_views_pct) && (
                <FormattedMessage
                  id="core.text.percent"
                  values={{ value: prePurchase?.avg_video_views_pct }}
                />
              )
            }
            elementName="pre-purchase-avg-views-pct"
            productMetricGroup="pre_purchase"
            productMetricValue="avg_video_views_pct"
            externalLink={externalLink}
          />
          {estimatedCpv && (
            <AnalyticsDetailCard
              title="submission.prePurchaseAnalytics.tiktok.estCPV"
              content={<AnalyticsCustomPrice currency={currency} quantity={estimatedCpv} />}
              elementName="pre-purchase-est-cpv"
              productMetricGroup="pre_purchase"
              productMetricValue="est_cpv"
              externalLink={externalLink}
            />
          )}

          {estimatedCpm && (
            <AnalyticsDetailCard
              title="submission.prePurchaseAnalytics.tiktok.estCPM"
              content={<AnalyticsCustomPrice currency={currency} quantity={estimatedCpm} />}
              elementName="pre-purchase-est-cpm"
              productMetricGroup="pre_purchase"
              productMetricValue="est_cpm"
              externalLink={externalLink}
              values={{
                link: (
                  <Link href={CPM_EXPLAINED} target="_blank" color={Theme.primaryColor} lowercase>
                    <FormattedMessage id="core.text.clickHere" />
                  </Link>
                ),
              }}
            />
          )}
        </AnalyticsPrePurchaseGrid>
      </Container>
    </>
  )
}

export default AnalyticsPrePurchaseTikTok
